// frontend/src/components/devices/Taskmanager/Performance/Performance.jsx
/**
 * Activity Watch — Foreground + Background Performance Monitor
 * 
 * Features:
 * - Active Application: currently in foreground
 * - Background Applications: used today but not currently in foreground
 * - Per-app metrics: Active Time + Idle Time + Background Open Time
 * - Activity Watch–style color-coded bars
 * - 2-minute idle threshold
 */

import React, { useEffect, useState, useCallback, useRef, useMemo } from "react";
import { useNavigate, useParams } from "react-router-dom";
import socket from "../../../../utils/socket";
import {
    ArrowLeft,
    Activity,
    Monitor,
    Clock,
    Layers,
    RefreshCw,
    AlertCircle,
    TrendingUp,
    BarChart3,
    Eye,
    EyeOff,
    Coffee,
    MousePointer,
    ArrowRightLeft,
    CalendarDays,
    PauseCircle,
    PlayCircle,
    Lock,
    ChevronDown,
    ChevronUp,
    AppWindow,
    Hourglass,
} from "lucide-react";
import "./performance.css";

// ============================
// FORMAT UTILITIES
// ============================
const formatSeconds = (totalSec) => {
    if (!totalSec || totalSec < 0) return "0s";
    const s = Math.floor(totalSec);
    const hours = Math.floor(s / 3600);
    const minutes = Math.floor((s % 3600) / 60);
    const seconds = s % 60;
    if (hours > 0) return `${hours}h ${minutes}m ${seconds}s`;
    if (minutes > 0) return `${minutes}m ${seconds}s`;
    return `${seconds}s`;
};

const formatSecondsCompact = (totalSec) => {
    if (!totalSec || totalSec < 0) return "0s";
    const s = Math.floor(totalSec);
    const hours = Math.floor(s / 3600);
    const minutes = Math.floor((s % 3600) / 60);
    if (hours > 0) return `${hours}h ${minutes}m`;
    if (minutes > 0) return `${minutes}m`;
    return `${s}s`;
};

const getAppDisplayName = (appName) => {
    if (!appName) return "Unknown";
    return appName.replace(/\.exe$/i, "");
};

const getAppCategory = (appName) => {
    const name = (appName || "").toLowerCase();
    if (/chrome|firefox|edge|brave|opera|safari/.test(name)) return "browser";
    if (/code|sublime|atom|notepad|vim|emacs|jetbrains|idea|pycharm|webstorm/.test(name)) return "editor";
    if (/cmd|powershell|terminal|windowsterminal|bash|wt/.test(name)) return "terminal";
    if (/slack|teams|discord|zoom|telegram|whatsapp/.test(name)) return "communication";
    if (/explorer|finder/.test(name)) return "filemanager";
    return "application";
};

const getCategoryIcon = (category) => {
    switch (category) {
        case "browser": return "🌐";
        case "editor": return "📝";
        case "terminal": return "⬛";
        case "communication": return "💬";
        case "filemanager": return "📁";
        default: return "📱";
    }
};

// Color palette for apps
const APP_COLORS = [
    '#34d399', '#60a5fa', '#a78bfa', '#f472b6', '#fbbf24',
    '#22d3ee', '#fb923c', '#818cf8', '#4ade80', '#f87171',
    '#c084fc', '#38bdf8', '#e879f9', '#facc15', '#2dd4bf',
];
const getAppColor = (index) => APP_COLORS[index % APP_COLORS.length];

// ============================
// COMPONENT
// ============================
const Performance = () => {
    const { id: agentId } = useParams();
    const navigate = useNavigate();

    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [device, setDevice] = useState(null);
    const [lastUpdate, setLastUpdate] = useState(null);
    const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split('T')[0]);

    // Foreground daily data
    const [dailyApps, setDailyApps] = useState([]);
    const [summary, setSummary] = useState({
        totalApps: 0, totalActiveSeconds: 0, totalIdleSeconds: 0,
        totalSessions: 0, totalVisits: 0, activeApp: null, topApps: []
    });
    const [currentForegroundApp, setCurrentForegroundApp] = useState(null);
    const [isUserIdle, setIsUserIdle] = useState(false);
    const [isSystemLocked, setIsSystemLocked] = useState(false);

    // Background / total usage data (from usage_tracker) — day-scoped
    const [usageData, setUsageData] = useState([]);
    // All-time usage data for Activity History (only used on today)
    const [allTimeUsageData, setAllTimeUsageData] = useState([]);

    // Live timer
    const timerRef = useRef(null);
    const [liveSeconds, setLiveSeconds] = useState(0);
    const foregroundStartRef = useRef(null);

    // ============================
    // DATA FETCHING
    // ============================
    const fetchFgData = useCallback(async () => {
        try {
            const token = sessionStorage.getItem("token");
            const headers = { "Authorization": `Bearer ${token}` };

            const [summaryRes, dailyRes] = await Promise.all([
                fetch(`${import.meta.env.VITE_BACKEND_URL}/api/performance/${agentId}/fg/summary?date=${selectedDate}`, { headers }),
                fetch(`${import.meta.env.VITE_BACKEND_URL}/api/performance/${agentId}/fg/daily?date=${selectedDate}`, { headers })
            ]);

            if (summaryRes.ok) {
                const d = await summaryRes.json();
                if (d.success) {
                    setSummary(d.data);
                    if (d.data.activeApp) setCurrentForegroundApp(d.data.activeApp);
                }
            }
            if (dailyRes.ok) {
                const d = await dailyRes.json();
                if (d.success) setDailyApps(d.data || []);
            }
            setLastUpdate(new Date());
        } catch (err) {
            console.error("Failed to fetch FTE data:", err);
        } finally {
            setLoading(false);
        }
    }, [agentId, selectedDate]);

    const fetchUsageData = useCallback(async () => {
        try {
            const token = sessionStorage.getItem("token");
            const res = await fetch(
                `${import.meta.env.VITE_BACKEND_URL}/api/performance/${agentId}/usage?date=${selectedDate}`,
                { headers: { "Authorization": `Bearer ${token}` } }
            );
            if (res.ok) {
                const d = await res.json();
                if (d.success && d.data) setUsageData(d.data.all || []);
            }
        } catch (err) {
            console.error("Failed to fetch usage data:", err);
        }
    }, [agentId, selectedDate]);

    const fetchDeviceInfo = useCallback(async () => {
        try {
            const token = sessionStorage.getItem("token");
            const res = await fetch(
                `${import.meta.env.VITE_BACKEND_URL}/api/task-manager/${agentId}`,
                { headers: { "Authorization": `Bearer ${token}` } }
            );
            const data = await res.json();
            if (data?.success && data?.data?.length) {
                const doc = data.data[0];
                setDevice({
                    hostname: doc.device?.hostname || `Agent ${doc.agentId}`,
                    os_type: doc.device?.os_type || "Unknown OS",
                    os_version: doc.device?.os_version || "",
                });
            }
        } catch (err) {
            console.error("Failed to fetch device info:", err);
        }
    }, [agentId]);

    // Fetch all-time usage data for Activity History (only needed for today)
    const fetchAllTimeUsageData = useCallback(async () => {
        try {
            const token = sessionStorage.getItem("token");
            const res = await fetch(
                `${import.meta.env.VITE_BACKEND_URL}/api/performance/${agentId}/usage`,
                { headers: { "Authorization": `Bearer ${token}` } }
            );
            if (res.ok) {
                const d = await res.json();
                if (d.success && d.data) setAllTimeUsageData(d.data.all || []);
            }
        } catch (err) {
            console.error("Failed to fetch all-time usage data:", err);
        }
    }, [agentId]);

    // ============================
    // EFFECTS
    // ============================
    useEffect(() => {
        if (!agentId) { setError("No agent identifier provided"); setLoading(false); return; }
        fetchFgData();
        fetchUsageData();
        fetchDeviceInfo();
        // Fetch all-time data for Activity History only when viewing today
        const today = new Date().toISOString().split('T')[0];
        if (selectedDate === today) fetchAllTimeUsageData();
    }, [agentId, selectedDate, fetchFgData, fetchUsageData, fetchDeviceInfo, fetchAllTimeUsageData]);

    useEffect(() => {
        const interval = setInterval(() => { fetchFgData(); fetchUsageData(); }, 30000);
        return () => clearInterval(interval);
    }, [fetchFgData, fetchUsageData]);

    // Live timer
    useEffect(() => {
        if (currentForegroundApp && !isUserIdle && !isSystemLocked) {
            timerRef.current = setInterval(() => setLiveSeconds(prev => prev + 1), 1000);
        } else {
            if (timerRef.current) clearInterval(timerRef.current);
        }
        return () => { if (timerRef.current) clearInterval(timerRef.current); };
    }, [currentForegroundApp, isUserIdle, isSystemLocked]);

    // Socket events
    useEffect(() => {
        const handleFocusGained = (data) => {
            if (data.agentId !== agentId) return;
            setCurrentForegroundApp({ appName: data.appName, lastTitle: data.windowTitle, lastVisitedAt: new Date() });
            setIsUserIdle(false); setIsSystemLocked(false); setLiveSeconds(0);
            foregroundStartRef.current = Date.now();
            setLastUpdate(new Date());
            setDailyApps(prev => prev.map(app => ({ ...app, isCurrentlyForeground: app.appName === data.appName })));
            setSummary(prev => ({ ...prev, activeApp: { appName: data.appName, lastTitle: data.windowTitle }, totalVisits: prev.totalVisits + 1 }));
        };

        const handleSessionClosed = (data) => {
            if (data.agentId !== agentId) return;
            if (currentForegroundApp?.appName === data.appName) { setCurrentForegroundApp(null); setLiveSeconds(0); foregroundStartRef.current = null; }

            setDailyApps(prev => {
                const existing = prev.find(a => a.appName === data.appName);
                if (existing) {
                    return prev.map(app => app.appName === data.appName ? {
                        ...app,
                        totalActiveSeconds: (app.totalActiveSeconds || 0) + (data.activeSeconds || 0),
                        totalIdleSeconds: (app.totalIdleSeconds || 0) + (data.idleSeconds || 0),
                        visitCount: (app.visitCount || 0) + 1,
                        lastTitle: data.windowTitle || app.lastTitle,
                        isCurrentlyForeground: false, lastVisitedAt: new Date()
                    } : app);
                }
                return [...prev, {
                    appName: data.appName,
                    totalActiveSeconds: data.activeSeconds || 0, totalIdleSeconds: data.idleSeconds || 0,
                    visitCount: 1, lastTitle: data.windowTitle || "", isCurrentlyForeground: false,
                    lastVisitedAt: new Date(), date: selectedDate
                }];
            });

            setSummary(prev => ({
                ...prev,
                totalActiveSeconds: prev.totalActiveSeconds + (data.activeSeconds || 0),
                totalIdleSeconds: prev.totalIdleSeconds + (data.idleSeconds || 0),
                totalApps: Math.max(prev.totalApps, dailyApps.length)
            }));
            setLastUpdate(new Date());
        };

        const handleIdleStart = (data) => { if (data.agentId === agentId) setIsUserIdle(true); };
        const handleIdleEnd = (data) => { if (data.agentId === agentId) setIsUserIdle(false); };
        const handleSystemLocked = (data) => { if (data.agentId === agentId) { setIsSystemLocked(true); setCurrentForegroundApp(null); setLiveSeconds(0); } };
        const handleSystemUnlocked = (data) => { if (data.agentId === agentId) setIsSystemLocked(false); };
        const handleSystemSuspended = (data) => { if (data.agentId === agentId) { setIsSystemLocked(true); setCurrentForegroundApp(null); setLiveSeconds(0); } };
        const handleSystemResumed = (data) => { if (data.agentId === agentId) setIsSystemLocked(false); };

        socket.on("fg_focus_gained", handleFocusGained);
        socket.on("fg_session_closed", handleSessionClosed);
        socket.on("fg_idle_start", handleIdleStart);
        socket.on("fg_idle_end", handleIdleEnd);
        socket.on("fg_system_locked", handleSystemLocked);
        socket.on("fg_system_unlocked", handleSystemUnlocked);
        socket.on("fg_system_suspended", handleSystemSuspended);
        socket.on("fg_system_resumed", handleSystemResumed);

        return () => {
            socket.off("fg_focus_gained", handleFocusGained);
            socket.off("fg_session_closed", handleSessionClosed);
            socket.off("fg_idle_start", handleIdleStart);
            socket.off("fg_idle_end", handleIdleEnd);
            socket.off("fg_system_locked", handleSystemLocked);
            socket.off("fg_system_unlocked", handleSystemUnlocked);
            socket.off("fg_system_suspended", handleSystemSuspended);
            socket.off("fg_system_resumed", handleSystemResumed);
        };
    }, [agentId, currentForegroundApp, selectedDate, dailyApps.length]);

    // ============================
    // MERGED DATA: foreground + usage tracker
    // ============================
    // Build a lookup from usage tracker data (total running time per app)
    const usageLookup = useMemo(() => {
        const map = {};
        usageData.forEach(u => {
            map[(u.appName || "").toLowerCase()] = {
                totalRunningSeconds: (u.totalUsageMs || 0) / 1000,
                isRunning: u.isActive || false,
            };
        });
        return map;
    }, [usageData]);

    // Enrich daily apps with background open time
    const enrichedApps = useMemo(() => {
        return dailyApps.map(app => {
            let activeS = app.totalActiveSeconds || 0;
            let idleS = app.totalIdleSeconds || 0;

            // If this app is currently in foreground, add the live timer seconds
            if (app.isCurrentlyForeground && !isSystemLocked) {
                if (isUserIdle) idleS += liveSeconds;
                else activeS += liveSeconds;
            }

            const key = (app.appName || "").toLowerCase();
            const usage = usageLookup[key];
            const totalRunning = usage ? usage.totalRunningSeconds : 0;

            // Background = total running time - foreground time (active + idle)
            const bgTime = Math.max(0, totalRunning - activeS - idleS);
            const isRunning = usage ? usage.isRunning : false;

            return {
                ...app,
                totalActiveSeconds: activeS,
                totalIdleSeconds: idleS,
                backgroundSeconds: bgTime,
                isRunning
            };
        });
    }, [dailyApps, usageLookup, liveSeconds, isUserIdle, isSystemLocked]);

    // Split into Active (currently foreground) and Inactive
    const activeApp = useMemo(() => {
        return enrichedApps.find(a => a.isCurrentlyForeground);
    }, [enrichedApps]);

    const sortedApps = useMemo(() => {
        return [...enrichedApps].sort((a, b) =>
            ((b.totalActiveSeconds || 0) + (b.totalIdleSeconds || 0) + (b.backgroundSeconds || 0)) -
            ((a.totalActiveSeconds || 0) + (a.totalIdleSeconds || 0) + (a.backgroundSeconds || 0))
        );
    }, [enrichedApps]);

    // Max total for bar scaling
    const maxTotal = useMemo(() => {
        return Math.max(...enrichedApps.map(a =>
            (a.totalActiveSeconds || 0) + (a.totalIdleSeconds || 0) + (a.backgroundSeconds || 0)
        ), 1);
    }, [enrichedApps]);

    // Totals
    const totalTime = summary.totalActiveSeconds + summary.totalIdleSeconds;
    const totalBgTime = useMemo(() => enrichedApps.reduce((s, a) => s + (a.backgroundSeconds || 0), 0), [enrichedApps]);

    // ============================
    // RENDER: COMPACT HERO STRIP
    // ============================
    const renderHeroStrip = () => {
        const app = currentForegroundApp;
        const isToday = selectedDate === new Date().toISOString().split('T')[0];

        return (
            <div className="fg-hero-strip">
                <div className="fg-hero-strip-left">
                    <div className="fg-hero-indicator">
                        {isSystemLocked ? <Lock size={14} className="fg-hero-lock-icon" />
                            : app ? <span className={`fg-hero-dot-sm ${isUserIdle ? 'idle' : 'active'}`} />
                                : <span className="fg-hero-dot-sm inactive" />}
                    </div>
                    {isSystemLocked ? (
                        <span className="fg-hero-strip-app">Screen Locked</span>
                    ) : app ? (
                        <>
                            <span className="fg-hero-strip-emoji">{getCategoryIcon(getAppCategory(app.appName))}</span>
                            <span className="fg-hero-strip-app">{getAppDisplayName(app.appName)}</span>
                            {app.lastTitle && <span className="fg-hero-strip-title" title={app.lastTitle}>{app.lastTitle}</span>}
                        </>
                    ) : (
                        <span className="fg-hero-strip-app muted">No active application</span>
                    )}
                </div>
                <div className="fg-hero-strip-right">
                    {app && !isSystemLocked && (
                        <div className={`fg-hero-strip-timer ${isUserIdle ? 'paused' : ''}`}>
                            {isUserIdle ? <PauseCircle size={14} /> : <PlayCircle size={14} />}
                            <span>{formatSeconds(liveSeconds)}</span>
                        </div>
                    )}
                    {isToday && (
                        <span className="fg-hero-live-badge-sm"><span className="fg-hero-live-blink" />LIVE</span>
                    )}
                </div>
            </div>
        );
    };

    // ============================
    // RENDER: STATS CARDS (matching URL Tracker sizing)
    // ============================
    const renderStatsCards = () => {
        const activePercent = totalTime > 0 ? Math.round((summary.totalActiveSeconds / totalTime) * 100) : 0;

        return (
            <div className="fg-stats-grid">
                <div className="fg-stat-card">
                    <div className="fg-stat-icon total"><Hourglass size={20} /></div>
                    <div className="fg-stat-info">
                        <span className="fg-stat-value">{formatSecondsCompact(totalTime)}</span>
                        <span className="fg-stat-label">Total Time</span>
                    </div>
                </div>
                <div className="fg-stat-card">
                    <div className="fg-stat-icon active"><MousePointer size={20} /></div>
                    <div className="fg-stat-info">
                        <span className="fg-stat-value">{formatSecondsCompact(summary.totalActiveSeconds)}</span>
                        <span className="fg-stat-label">Active Time</span>
                    </div>
                </div>
                <div className="fg-stat-card">
                    <div className="fg-stat-icon idle"><Coffee size={20} /></div>
                    <div className="fg-stat-info">
                        <span className="fg-stat-value">{formatSecondsCompact(summary.totalIdleSeconds)}</span>
                        <span className="fg-stat-label">Idle Time</span>
                    </div>
                </div>
                <div className="fg-stat-card">
                    <div className="fg-stat-icon productivity"><TrendingUp size={20} /></div>
                    <div className="fg-stat-info">
                        <span className="fg-stat-value">{activePercent}%</span>
                        <span className="fg-stat-label">Work Rate</span>
                    </div>
                    <div className="fg-stat-bar"><div className="fg-stat-bar-fill" style={{ width: `${activePercent}%` }} /></div>
                </div>
            </div>
        );
    };

    // ============================
    // RENDER: ACTIVITY TIMELINE
    // ============================
    const renderTimeline = () => {
        if (enrichedApps.length === 0 || (totalTime + totalBgTime) === 0) return null;
        const grandTotal = totalTime + totalBgTime;

        return (
            <div className="fg-aw-section">
                <div className="fg-section-header">
                    <BarChart3 size={20} className="fg-section-icon" />
                    <h2>Activity Timeline</h2>
                    <div className="fg-aw-legend">
                        <span className="fg-aw-legend-item"><span className="fg-aw-legend-dot active" />Active</span>
                        <span className="fg-aw-legend-item"><span className="fg-aw-legend-dot idle" />Idle</span>
                        <span className="fg-aw-legend-item"><span className="fg-aw-legend-dot bg" />Background</span>
                    </div>
                </div>
                <div className="fg-aw-bar-container">
                    <div className="fg-aw-bar">
                        {enrichedApps.map((app, idx) => {
                            const activeW = ((app.totalActiveSeconds || 0) / grandTotal) * 100;
                            const idleW = ((app.totalIdleSeconds || 0) / grandTotal) * 100;
                            const bgW = ((app.backgroundSeconds || 0) / grandTotal) * 100;
                            const color = getAppColor(idx);
                            return (
                                <React.Fragment key={app.appName}>
                                    {activeW > 0.3 && <div className="fg-aw-segment active" style={{ width: `${activeW}%`, backgroundColor: color }} title={`${getAppDisplayName(app.appName)} — Active: ${formatSecondsCompact(app.totalActiveSeconds)}`} />}
                                    {idleW > 0.3 && <div className="fg-aw-segment idle" style={{ width: `${idleW}%`, backgroundColor: color, opacity: 0.45 }} title={`${getAppDisplayName(app.appName)} — Idle: ${formatSecondsCompact(app.totalIdleSeconds)}`} />}
                                    {bgW > 0.3 && <div className="fg-aw-segment bg" style={{ width: `${bgW}%`, backgroundColor: color, opacity: 0.2 }} title={`${getAppDisplayName(app.appName)} — Background: ${formatSecondsCompact(app.backgroundSeconds)}`} />}
                                </React.Fragment>
                            );
                        })}
                    </div>
                    <div className="fg-aw-bar-labels">
                        <span>0s</span>
                        <span>{formatSecondsCompact(grandTotal)}</span>
                    </div>
                </div>
                <div className="fg-aw-app-legend">
                    {enrichedApps.slice(0, 10).map((app, idx) => (
                        <span key={app.appName} className="fg-aw-app-chip">
                            <span className="fg-aw-app-dot" style={{ backgroundColor: getAppColor(idx) }} />
                            {getAppDisplayName(app.appName)}
                        </span>
                    ))}
                </div>
            </div>
        );
    };

    // ============================
    // RENDER: APP ROW (reused for active & inactive)
    // ============================
    const renderAppRow = (app, idx, globalIdx) => {
        const activeS = app.totalActiveSeconds || 0;
        const idleS = app.totalIdleSeconds || 0;
        const bgS = app.backgroundSeconds || 0;
        const usageS = activeS + idleS; // Usage = Active + Idle combined
        const usageBarW = maxTotal > 0 ? (usageS / maxTotal) * 100 : 0;
        const bgBarW = maxTotal > 0 ? (bgS / maxTotal) * 100 : 0;
        const category = getAppCategory(app.appName);
        const appColor = getAppColor(globalIdx);
        const switches = app.visitCount || app.sessionCount || 0;

        return (
            <div key={`${app.appName}-${idx}`} className={`fg-app-row ${app.isCurrentlyForeground ? 'foreground' : ''}`}>
                <div className="fg-app-rank"><span className="fg-rank-num">#{idx + 1}</span></div>
                <div className="fg-app-icon-col">
                    <span className="fg-app-emoji">{getCategoryIcon(category)}</span>
                    {app.isCurrentlyForeground && <span className="fg-app-live-dot" />}
                </div>
                <div className="fg-app-info-col">
                    <div className="fg-app-name-row">
                        <span className="fg-app-name">{getAppDisplayName(app.appName)}</span>
                        {app.isCurrentlyForeground && <span className="fg-app-active-tag">Active</span>}
                        {app.isRunning && !app.isCurrentlyForeground && <span className="fg-app-running-badge">Running</span>}
                    </div>
                    {app.lastTitle && <span className="fg-app-title" title={app.lastTitle}>{app.lastTitle}</span>}
                </div>
                <div className="fg-app-metrics">
                    <div className="fg-app-metric usage-metric">
                        <span className="fg-metric-value">{formatSecondsCompact(usageS)}</span>
                        <span className="fg-metric-label">Usage</span>
                    </div>
                    <div className="fg-app-metric active">
                        <span className="fg-metric-value">{formatSecondsCompact(activeS)}</span>
                        <span className="fg-metric-label">Active</span>
                    </div>
                    <div className="fg-app-metric idle">
                        <span className="fg-metric-value">{formatSecondsCompact(idleS)}</span>
                        <span className="fg-metric-label">Idle</span>
                    </div>
                </div>
                <div className="fg-app-bar-col">
                    <div className="fg-app-bar-with-value">
                        <span className="fg-app-bar-value">{formatSecondsCompact(usageS)}</span>
                        <div className="fg-app-bar">
                            <div className="fg-app-bar-active" style={{ width: `${usageBarW}%`, backgroundColor: appColor }} />
                            <div className="fg-app-bar-bg" style={{ width: `${bgBarW}%`, left: `${usageBarW}%`, backgroundColor: appColor, opacity: 0.18 }} />
                        </div>
                    </div>
                </div>
            </div>
        );
    };

    // ============================
    // RENDER: ACTIVE APPLICATION SECTION (with live indicator)
    // ============================
    const renderActiveApplication = () => {
        const isToday = selectedDate === new Date().toISOString().split('T')[0];

        return (
            <div className="fg-section fg-active-section">
                <div className="fg-section-header">
                    <Monitor size={20} className="fg-section-icon fg-active-icon" />
                    <h2>Active Application</h2>
                    {isToday && activeApp && (
                        <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginLeft: 'auto' }}>
                            {isSystemLocked ? (
                                <span className="fg-count-badge" style={{ background: '#ef4444', color: '#fff' }}>
                                    <Lock size={12} style={{ marginRight: '4px' }} />Locked
                                </span>
                            ) : isUserIdle ? (
                                <span className="fg-count-badge" style={{ background: '#f59e0b', color: '#000' }}>
                                    <Coffee size={12} style={{ marginRight: '4px' }} />Idle
                                </span>
                            ) : (
                                <span className="fg-hero-live-badge-sm">
                                    <span className="fg-hero-live-blink" />LIVE
                                </span>
                            )}
                        </div>
                    )}
                    {isToday && !activeApp && !isSystemLocked && (
                        <span className="fg-count-badge" style={{ marginLeft: 'auto', opacity: 0.6 }}>No Foreground App</span>
                    )}
                </div>

                {activeApp ? (
                    <div className="fg-app-list">
                        <div className="fg-app-row fg-app-row-header">
                            <div className="fg-app-rank"><span className="fg-header-text">#</span></div>
                            <div className="fg-app-icon-col"></div>
                            <div className="fg-app-info-col"><span className="fg-header-text">Application</span></div>
                            <div className="fg-app-metrics">
                                <div className="fg-app-metric"><span className="fg-header-text">Usage</span></div>
                                <div className="fg-app-metric"><span className="fg-header-text">Active</span></div>
                                <div className="fg-app-metric"><span className="fg-header-text">Idle</span></div>
                            </div>
                        </div>
                        {renderAppRow(activeApp, 0, enrichedApps.indexOf(activeApp))}
                    </div>
                ) : (
                    <div className="fg-empty">
                        <Monitor size={36} />
                        <p>No application currently in foreground</p>
                    </div>
                )}
            </div>
        );
    };

    // ============================
    // RENDER: BACKGROUND APPLICATIONS SECTION
    // ============================
    const renderInactiveApplications = () => (
        <div className="fg-section fg-inactive-section">
            <div className="fg-section-header">
                <Layers size={20} className="fg-section-icon fg-inactive-icon" />
                <h2>Applications</h2>
                <span className="fg-count-badge">{sortedApps.length}</span>
            </div>

            {sortedApps.length === 0 ? (
                <div className="fg-empty">
                    <AppWindow size={36} />
                    <p>No Applications recorded for {selectedDate}</p>
                </div>
            ) : (
                <div className="fg-app-list">
                    <div className="fg-app-row fg-app-row-header">
                        <div className="fg-app-rank"><span className="fg-header-text">#</span></div>
                        <div className="fg-app-icon-col"></div>
                        <div className="fg-app-info-col"><span className="fg-header-text">Application</span></div>
                        <div className="fg-app-metrics">
                            <div className="fg-app-metric"><span className="fg-header-text">Usage</span></div>
                            <div className="fg-app-metric"><span className="fg-header-text">Active</span></div>
                            <div className="fg-app-metric"><span className="fg-header-text">Idle</span></div>
                        </div>
                    </div>
                    {sortedApps.map((app, idx) => renderAppRow(app, idx, enrichedApps.indexOf(app)))}
                </div>
            )}
        </div>
    );

    // ============================
    // RENDER: ACTIVITY HISTORY (all-time, only shown on today)
    // ============================
    const sortedAllTimeHistory = useMemo(() => {
        return [...allTimeUsageData].sort((a, b) => (b.totalUsageMs || 0) - (a.totalUsageMs || 0));
    }, [allTimeUsageData]);

    const maxAllTimeMs = useMemo(() => Math.max(...sortedAllTimeHistory.map(a => a.totalUsageMs || 0), 1), [sortedAllTimeHistory]);

    const renderActivityHistory = () => {
        if (sortedAllTimeHistory.length === 0) return null;

        return (
            <div className="fg-section fg-history-section">
                <div className="fg-section-header">
                    <Layers size={20} className="fg-section-icon fg-history-icon" />
                    <h2>Activity History</h2>
                    <span className="fg-count-badge fg-history-badge">{sortedAllTimeHistory.length} apps</span>
                </div>
                <div className="fg-app-list">
                    <div className="fg-app-row fg-app-row-header">
                        <div className="fg-app-rank"><span className="fg-header-text">#</span></div>
                        <div className="fg-app-icon-col"></div>
                        <div className="fg-app-info-col"><span className="fg-header-text">Application</span></div>
                        <div className="fg-app-metrics">
                            <div className="fg-app-metric"><span className="fg-header-text">Total Time</span></div>
                            <div className="fg-app-metric"><span className="fg-header-text">Active</span></div>
                            <div className="fg-app-metric"><span className="fg-header-text">Idle</span></div>
                        </div>
                    </div>
                    {sortedAllTimeHistory.map((app, idx) => {
                        const totalSec = (app.totalUsageMs || 0) / 1000;
                        const barW = maxAllTimeMs > 0 ? ((app.totalUsageMs || 0) / maxAllTimeMs) * 100 : 0;
                        const color = getAppColor(idx);
                        const category = getAppCategory(app.appName);

                        // Cross-reference foreground data for active/idle/bg
                        const fgMatch = enrichedApps.find(e => (e.appName || "").toLowerCase() === (app.appName || "").toLowerCase());
                        const activeS = fgMatch ? (fgMatch.totalActiveSeconds || 0) : 0;
                        const idleS = fgMatch ? (fgMatch.totalIdleSeconds || 0) : 0;
                        const bgS = fgMatch ? (fgMatch.backgroundSeconds || 0) : Math.max(0, totalSec);
                        const hasFgData = !!fgMatch;

                        return (
                            <div key={`hist-${app.appName}-${idx}`} className="fg-app-row">
                                <div className="fg-app-rank"><span className="fg-rank-num">#{idx + 1}</span></div>
                                <div className="fg-app-icon-col">
                                    <span className="fg-app-emoji">{getCategoryIcon(category)}</span>
                                </div>
                                <div className="fg-app-info-col">
                                    <div className="fg-app-name-row">
                                        <span className="fg-app-name">{getAppDisplayName(app.appName)}</span>
                                        {fgMatch?.isCurrentlyForeground && <span className="fg-app-active-tag">Active</span>}
                                        {app.isActive && !fgMatch?.isCurrentlyForeground && <span className="fg-app-running-badge">Running</span>}
                                    </div>
                                    {app.lastTitle && <span className="fg-app-title" title={app.lastTitle}>{app.lastTitle}</span>}
                                </div>
                                <div className="fg-app-metrics">
                                    <div className="fg-app-metric usage-metric">
                                        <span className="fg-metric-value">{formatSecondsCompact(totalSec)}</span>
                                        <span className="fg-metric-label">Total</span>
                                    </div>
                                    <div className="fg-app-metric active">
                                        <span className="fg-metric-value">{hasFgData ? formatSecondsCompact(activeS) : '—'}</span>
                                        <span className="fg-metric-label">Active</span>
                                    </div>
                                    <div className="fg-app-metric idle">
                                        <span className="fg-metric-value">{hasFgData ? formatSecondsCompact(idleS) : '—'}</span>
                                        <span className="fg-metric-label">Idle</span>
                                    </div>
                                </div>
                                <div className="fg-app-bar-col">
                                    <div className="fg-app-bar-with-value">
                                        <span className="fg-app-bar-value">{formatSecondsCompact(totalSec)}</span>
                                        <div className="fg-app-bar">
                                            <div className="fg-app-bar-active" style={{ width: `${barW}%`, backgroundColor: color }} />
                                        </div>
                                    </div>
                                </div>
                            </div>
                        );
                    })}
                </div>
            </div>
        );
    };

    // ============================
    // MAIN RENDER
    // ============================
    if (loading) {
        return (
            <div className="fg-container">
                <div className="fg-loading">
                    <RefreshCw size={40} className="animate-spin" style={{ color: '#60a5fa' }} />
                    <span>Initializing Activity Watch...</span>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="fg-container">
                <div className="fg-error">
                    <AlertCircle size={48} style={{ color: '#ef4444' }} />
                    <p>{error}</p>
                    <button className="gc-icon-btn" onClick={() => navigate("/devices")} data-tip="Back to Devices" aria-label="Back to Devices"><ArrowLeft size={16} /></button>
                </div>
            </div>
        );
    }

    // Determine if this is today and if agent has any data
    const isToday = selectedDate === new Date().toISOString().split('T')[0];
    const hasAnyData = dailyApps.length > 0 || usageData.length > 0;

    return (
        <div className="fg-container">
            {/* Header */}
            <div className="fg-header">
                <div className="fg-header-left">
                    <button className="gc-icon-btn" onClick={() => navigate("/devices")} data-tip="Back to Devices" aria-label="Back to Devices"><ArrowLeft size={16} /></button>
                    <div className="fg-title-group">
                        <h1>Activity Watch</h1>
                        <p>Foreground tracking for <strong>{device?.hostname || agentId}</strong></p>
                    </div>
                </div>
                <div className="fg-header-right">
                    <div className="fg-header-meta">
                        <p><strong>Updated:</strong> {lastUpdate?.toLocaleTimeString()}</p>
                    </div>
                    <div className="fg-date-picker">
                        <CalendarDays size={16} />
                        <input type="date" value={selectedDate} onChange={(e) => setSelectedDate(e.target.value)} max={new Date().toISOString().split('T')[0]} />
                    </div>
                </div>
            </div>

            {hasAnyData ? (
                <>
                    {/* Stats Cards */}
                    {(dailyApps.length > 0 || (isToday && currentForegroundApp)) && renderStatsCards()}

                    {/* Active Application with live indicator — only for today */}
                    {isToday && renderActiveApplication()}

                    {/* Applications (non-foreground) */}
                    {renderInactiveApplications()}

                    {/* Activity History — all-time, only on today */}
                    {isToday && renderActivityHistory()}
                </>
            ) : (
                <div className="fg-offline-banner">
                    <Monitor size={20} />
                    <span>No Data Available for {selectedDate}</span>
                </div>
            )}
        </div>
    );
};

export default Performance;
